import React, { useContext, useEffect, useState } from 'react';
import { Button, Form, Modal, Select, Tabs } from 'antd';

import RichTextInput from '@totem/components/common/richText/RichTextInput';
import ExtendedFields from '@totem/components/ticketing/details/activities/extendedFields';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import {
  Activity,
  ActivityConfiguration,
  ExtendedFieldValue,
} from '@totem/components/ticketing/ticketing';
import { ActivityCreateExtras } from '@totem/components/ticketing/types';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import { Attribute } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const FormItem = Form.Item;
const { Option } = Select;
const { TabPane } = Tabs;

const styles = {
  form: {
    width: '100%',
  },
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
  },
};

type Props = {
  staticActivityCode?: string;
};

const NaActivityEditDialog = ({ staticActivityCode }: Props) => {
  const {
    onAction,
    onCreateActivity,
    activityConfiguration,
    action,
    actionExtras,
  } = useContext(TicketContext);
  const [note, setNote] = useState<string>('');
  const [activityCode, setActivityCode] = useState<string>(
    isNotNull(staticActivityCode) ? staticActivityCode : '',
  );
  const [selectedActivityCode, setSelectedActivityCode] =
    useState<ActivityConfiguration>(null);
  const [selectedTab, setSelectedTab] = useState<any>('tabNotes');
  const [extendedFieldValues, setExtendedFieldValues] =
    useState<Map<string, ExtendedFieldValue>>(null);
  const dialogOpen =
    action === 'show-activity-create' ||
    action === 'show-activity-edit' ||
    action === 'show-party-involved-create' ||
    action === 'show-queue-assignment-create' ||
    action === 'show-static-activity-dialog';

  const getEligibleActivityById = (activityId: string) => {
    const activityConfig = activityConfiguration.find(
      (activity) => activity.id === activityId,
    );
    if (typeof activityConfig !== 'undefined') {
      return activityConfig;
    }
    return null;
  };

  useEffect(() => {
    const activityConfig = getEligibleActivityById(activityCode);
    setSelectedActivityCode(activityConfig);

    const newExtendedFieldsValues = new Map<string, ExtendedFieldValue>();
    if (
      activityConfig !== null &&
      activityConfig.extendedFieldGroups !== null &&
      activityConfig.extendedFieldGroups.length > 0
    ) {
      setSelectedTab(activityConfig.extendedFieldGroups[0].name);

      activityConfig.extendedFieldGroups.forEach((group) => {
        if (group.extendedFields !== null && group.extendedFields.length > 0) {
          group.extendedFields.forEach((extFld) => {
            const extFldValue: ExtendedFieldValue = {
              key: `${group.name}:${extFld.name}`,
              name: extFld.name,
              label: extFld.label,
              value: '',
              isValid: true,
              display: extFld.display,
              id: '',
            };
            newExtendedFieldsValues.set(
              `${group.name}:${extFld.name}`,
              extFldValue,
            );
          });
        }
      });
    }
    setExtendedFieldValues(newExtendedFieldsValues);
  }, [activityCode, activityConfiguration]);

  const validate = () => {
    let isValid = true;
    if (selectedActivityCode === null) {
      return false;
    }
    if (
      isNotNull(selectedActivityCode) &&
      isNotNull(selectedActivityCode.extendedFieldGroups)
    ) {
      selectedActivityCode.extendedFieldGroups.forEach((group) => {
        if (group.extendedFields !== null && group.extendedFields.length > 0) {
          group.extendedFields.forEach((extFld) => {
            const foundValue = extendedFieldValues.get(
              `${group.name}:${extFld.name}`,
            );
            if (extFld.isRequired && foundValue.value.trim() === '') {
              isValid = false;
            }
            if (!foundValue.isValid) {
              isValid = false;
            }
          });
        }
      });
    }

    return isValid;
  };

  const handleActivitySubmit = () => {
    if (validate()) {
      const attributes = [] as Attribute[];
      if (extendedFieldValues !== null) {
        extendedFieldValues.forEach((val) => {
          const attribute: Attribute = {
            name: val.name,
            label: val.label,
            value: val.value,
            display: val.display,
            id: val.id,
          };
          attributes.push(attribute);
        });
      }

      const activity: Activity = {
        id: '',
        type: {
          id: selectedActivityCode.id,
          code: selectedActivityCode.code,
          display: selectedActivityCode.display,
        },
        notes: {
          isRichText: true,
          plainText: note,
          htmlText: note,
        },
        attributes,
        createdByType: 'User',
        createdByName: '',
        createdById: '',
        createdAt: new Date().toISOString(),
        isPrivate: false,
        keys: [],
      };

      if (isNotNull(actionExtras)) {
        try {
          const extras: ActivityCreateExtras = JSON.parse(actionExtras);

          if (extras.objectReference !== null) {
            activity.objectReference = extras.objectReference;
          }
        } catch {
          /* empty */
        }
      }

      onCreateActivity(activity);
    }
  };

  const handleActivityCodeChange = (value: string) => {
    setSelectedTab('tabNotes');

    setActivityCode(value);
  };

  const handleExtendedFieldChange = (
    groupName: string,
    fieldName: string,
    value: string,
    id: string,
    isValid: boolean,
  ) => {
    const key = `${groupName}:${fieldName}`;
    const newExtendedFieldValues = new Map(extendedFieldValues);
    const originalValue = newExtendedFieldValues.get(key);
    originalValue.value = value;

    originalValue.id = id;

    originalValue.isValid = isValid;

    newExtendedFieldValues.set(key, originalValue);

    setExtendedFieldValues(newExtendedFieldValues);
  };

  return (
    <Modal
      open={dialogOpen}
      onCancel={() => onAction('close-activity-dialog', '')}
      title={<ModalTitle>Activity</ModalTitle>}
      confirmLoading={false}
      width={'40%'}
      footer={
        <ModalFooter>
          <Button onClick={() => onAction('close-activity-dialog', '')}>
            Close
          </Button>
          <Button type="primary" onClick={handleActivitySubmit}>
            Save
          </Button>
        </ModalFooter>
      }
    >
      <Form layout="vertical" style={styles.form}>
        <div
          style={{ display: isNotNull(staticActivityCode) ? 'none' : 'inline' }}
        >
          <FormItem label="Activity Type" colon={false} style={styles.formItem}>
            <Select value={activityCode} onChange={handleActivityCodeChange}>
              <Option value="">--Select--</Option>
              {isNotNull(activityConfiguration) &&
                activityConfiguration
                  .sort((compA, compB) =>
                    sortStringAscending(compA.display, compB.display),
                  )
                  .map((ea) => (
                    <Option key={ea.id} value={ea.id}>
                      {ea.display}
                    </Option>
                  ))}
            </Select>
          </FormItem>
        </div>
        <Tabs activeKey={selectedTab} onChange={(key) => setSelectedTab(key)}>
          {selectedActivityCode !== null &&
            selectedActivityCode.extendedFieldGroups !== null &&
            selectedActivityCode.extendedFieldGroups.length > 0 &&
            selectedActivityCode.extendedFieldGroups.map(
              (extendedFieldGroup) => (
                <TabPane
                  tab={<b>{extendedFieldGroup.display}</b>}
                  key={extendedFieldGroup.name}
                >
                  <ExtendedFields
                    extendedFieldsGroup={extendedFieldGroup}
                    extendedFieldValues={extendedFieldValues}
                    onChange={handleExtendedFieldChange}
                  />
                </TabPane>
              ),
            )}
          <TabPane tab={<b>Notes</b>} key="tabNotes">
            <FormItem label="Notes" colon={false} style={styles.formItem}>
              <RichTextInput
                value={note}
                onChange={(value) => setNote(value)}
              />
            </FormItem>
          </TabPane>
        </Tabs>
      </Form>
    </Modal>
  );
};

export default NaActivityEditDialog;
